import { Button, Modal, Progress } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';

// ── Typewriter cycling sentences shown while API call is pending ──────────────
const PREP_SENTENCES = [
    'Reviewing the list.',
    'Validating the template.',
    'Starting message delivery process.',
];
const TYPE_SPEED = 45;   // ms per character (typing)
const PAUSE_AFTER = 1200; // ms to hold the fully-typed sentence
const ERASE_SPEED = 20;   // ms per character (erasing)

const TypewriterCycler = () => {
    const [displayed, setDisplayed] = useState('');
    const [sentenceIdx, setSentenceIdx] = useState(0);
    const [charIdx, setCharIdx] = useState(0);
    const [phase, setPhase] = useState('typing'); // 'typing' | 'pausing' | 'erasing'
    const timerRef = useRef(null);

    useEffect(() => {
        const sentence = PREP_SENTENCES[sentenceIdx];

        const tick = () => {
            if (phase === 'typing') {
                if (charIdx < sentence.length) {
                    setDisplayed(sentence.slice(0, charIdx + 1));
                    setCharIdx(c => c + 1);
                } else {
                    setPhase('pausing');
                }
            } else if (phase === 'pausing') {
                setPhase('erasing');
            } else if (phase === 'erasing') {
                if (displayed.length > 0) {
                    setDisplayed(d => d.slice(0, -1));
                } else {
                    setCharIdx(0);
                    setSentenceIdx(i => (i + 1) % PREP_SENTENCES.length);
                    setPhase('typing');
                }
            }
        };

        const delay =
            phase === 'typing' ? TYPE_SPEED :
                phase === 'pausing' ? PAUSE_AFTER :
                    ERASE_SPEED;

        timerRef.current = setTimeout(tick, delay);
        return () => clearTimeout(timerRef.current);
    }, [phase, charIdx, displayed, sentenceIdx]);

    return (
        <div style={styles.typewriterWrap}>
            <span style={styles.typewriterText}>
                {displayed}
                <span style={styles.cursor}>|</span>
            </span>
        </div>
    );
};

const styles = {
    typewriterWrap: {
        marginTop: 20,
        minHeight: 32,
        textAlign: 'center',
    },
    typewriterText: {
        fontSize: 14,
        color: '#595959',
        fontStyle: 'italic',
        letterSpacing: 0.3,
    },
    cursor: {
        display: 'inline-block',
        marginLeft: 2,
        animation: 'twBlink 0.75s step-end infinite',
        color: '#1677ff',
        fontStyle: 'normal',
        fontWeight: 'bold',
    },
};
// ─────────────────────────────────────────────────────────────────────────────

const ProgressModal = (props) => {
    const {
        progress,
        isModalVisible,
        loading,
        conicColors,
        reportProgress,
        resetCampaign
    } = props;
    const navigate = useNavigate();

    const count = reportProgress !== undefined && reportProgress !== null
        ? (progress >= 100 ? reportProgress : progress)
        : progress;

    const currentProgress = count;

    const shouldShowButtons = !loading &&
        ((reportProgress !== undefined && reportProgress !== null && reportProgress >= 100) ||
            (progress >= 100 && (reportProgress === undefined || reportProgress === null)));

    const getModalTitle = () => {
        if (progress >= 100 && reportProgress !== undefined && reportProgress !== null) {
            return reportProgress >= 100 ? "Campaign & Reports Completed" : "Generating Campaign Reports";
        }
        return progress >= 100 ? "Campaign Execution Completed" : "Campaign Execution In Progress";
    };

    return (
        <>
            {/* Inject blink keyframe once */}
            <style>{`
                @keyframes twBlink {
                    0%, 100% { opacity: 1; }
                    50%       { opacity: 0; }
                }
            `}</style>

            <Modal
                title={
                    <div style={{ textAlign: 'center', width: '100%' }}>
                        {getModalTitle()}
                    </div>
                }
                open={isModalVisible}
                footer={null}
                closable={true}
                onCancel={resetCampaign}
            >
                <div className="d-flex justify-content-center align-items-center flex-column mt-3">


                    {/* Typewriter cycling — shown while API call is pending or progress is zero */}
                    {(loading || Math.round(currentProgress) === 0) ? <TypewriterCycler />
                        :
                        <Progress
                            type="circle"
                            percent={Math.round(currentProgress)}
                            strokeColor={conicColors}
                            size={120}
                        />
                    }

                    {/* Action Buttons */}
                    {shouldShowButtons && (
                        <div className="d-flex justify-content-center mt-2 gap-2">
                            <Button
                                onClick={() => navigate('/campaign-history')}
                                type="primary"
                            >
                                View Report
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ProgressModal;
